import React from 'react';
import max from 'lodash/max';
import round from 'lodash/round';
import sortBy from 'lodash/sortBy';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import GeneralChart from '@/components/job-title/GeneralChart';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import { JobTitleContext } from '@/context/create-context';
import { useClintMediaQuery } from '@/utils/hooks';
import {
  TAB_JOB_FUTURE,
  WORK_EXPERIENCE_FUTURE_SEO_MAP,
  WORK_EXPERIENCE_MAP,
} from '@/constants/job-title';

const JobFutureSalaryCard = () => {
  const {
    jobTitle: {
      salary_details: {
        avg_value_by_experience_details: avgValueByExp,
        avg_growth_rate_details: { by_experience: avgGrowthRateByExp },
      },
      city_name: cityName = '',
    },
    displayName,
  } = React.useContext(JobTitleContext);
  const isMobile = useClintMediaQuery();
  const barChartRef = React.useRef(null);

  const seoValue = React.useMemo(() => {
    const futureSalaryData: string[] = [];
    const sortKeys = sortBy(Object.keys(avgValueByExp), (item) =>
      Object.keys(WORK_EXPERIENCE_MAP).indexOf(item),
    );
    sortKeys.map((experience) => {
      futureSalaryData.push(
        `${WORK_EXPERIENCE_FUTURE_SEO_MAP[experience]}薪资为¥${round(
          (avgValueByExp[experience].min + avgValueByExp[experience].max) / 2,
          -2,
        )}/月`,
      );
    });
    return futureSalaryData;
  }, [avgValueByExp]);

  const barConfig = React.useMemo<ECOption>(() => {
    const sortKeys = sortBy(Object.keys(avgValueByExp), (item) =>
      Object.keys(WORK_EXPERIENCE_MAP).indexOf(item),
    );
    const chartData = sortKeys.map((experience) => ({
      value: round(
        (avgValueByExp[experience].min + avgValueByExp[experience].max) / 2,
        -2,
      ),
      category: WORK_EXPERIENCE_MAP[experience],
    }));
    const values = chartData.map((item) => item.value);
    const maxVal = (max(values) || 0) * 1.1;

    return {
      grid: { show: false, top: '1%', bottom: '10%', left: '2%', right: '2%' },
      xAxis: {
        type: 'category',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: 'rgba(0,0,0,0.85)' },
        data: chartData.map((item) => item.category),
      },
      yAxis: {
        type: 'value',
        show: false,
        min: 0,
        max: maxVal * 1.1,
        data: chartData.map((item) => item.value),
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: {
            width: isMobile ? (chartData.length > 4 ? 60 : 86) : 100,
            opacity: 0.3,
          },
        },
        formatter: '￥{c}',
        padding: 0,
        borderWidth: 0,
        backgroundColor: 'transparent',
        shadowColor: 'transparent',
        position: (point) => [point[0] - 30, point[1] - 25],
        textStyle: {
          fontSize: 13,
          fontWeight: 500,
          color: '#008489',
          textAlign: 'center',
        },
      },
      series: [
        {
          type: 'bar',
          data: chartData,
          barMaxWidth: 65,
          cursor: 'default',
          emphasis: { disabled: true },
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: '#bde0e1',
                },
                {
                  offset: 1,
                  color: '#eef7f7',
                },
              ],
            },
          },
        },
        {
          type: 'line',
          data: chartData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#008489',
          },
        },
      ],
    };
  }, [avgValueByExp, isMobile]);

  return (
    <GeneralChart
      type="custom"
      title={`${cityName}不同工作年限的${displayName}的最新薪资是多少？`}
      subTitle="最新薪资"
      description={
        avgGrowthRateByExp && avgGrowthRateByExp.value ? (
          <DataInterpretationSEO
            lineClamp="line-clamp-5 tablet:line-clamp-3"
            className="font-normal text-opacity-85"
            dataEleGroup={`${TAB_JOB_FUTURE}_salary`}
          >
            {`工作年限对于${cityName}${displayName}岗位的收入有多大影响？${`${cityName}${displayName}`}的年平均涨薪幅度为${round(
              avgGrowthRateByExp.value * 100,
            )}%，${seoValue.join(
              '，',
            )}。曲线越向上代表薪资越高，数据来源依据于各平台公开薪酬数据，时间不同可能会产生波动，仅供参考。`}
          </DataInterpretationSEO>
        ) : (
          <></>
        )
      }
    >
      <div className="h-72 mb-4 mx-1 tablet:mx-0">
        <CusEcharts chartRef={barChartRef} config={barConfig} />
      </div>
    </GeneralChart>
  );
};

export default JobFutureSalaryCard;
